import {Component, ElementRef, Input, ViewChild} from '@angular/core';
import {RspEndpointsService} from '@rsp/shared/services/rest/rsp-endpoints.service';
import {ProductionSiteDTO} from '@rsp/models/dto/tenants-details.dto';
import {ImageUrlBuilderService} from '@rsp/shared/services/image-url-builder.service';
import {MatDialog} from '@angular/material/dialog';
import {CropperDialogComponent} from '@rsp/shared/dialog/cropper/cropper-dialog.component';
import {DefaultImages} from '@rsp/shared/util/default-images';
import {MatDialogRef} from '@angular/material/dialog/dialog-ref';
import {NotificationService} from '@rsp/shared/services/notification.service';
import {UserTrackingService} from '@rsp/shared/services/user-tracking.service';


export interface CroppingData {
  width: number;
  height: number;
  imageUrl: string;
  mimeType: string;
  fileName: string;
}

@Component({
  selector: 'rsp-image-uploader',
  templateUrl: './image-uploader.component.html',
  styleUrls: ['./image-uploader.component.scss']
})

export class ImageUploaderComponent {

  constructor(private rspEndpointService: RspEndpointsService,
              public imageUrlBuilder: ImageUrlBuilderService,
              public dialog: MatDialog,
              private userTracker: UserTrackingService,
              private notificationService: NotificationService) {
  }


  @Input()
  public set plant(dto: ProductionSiteDTO) {
    if (!dto) {
      return;
    }

    this._plant = dto;
    this.originalPicture = this.createImageUrl(dto.id);
  }

  public get plant(): ProductionSiteDTO {
    return this._plant;
  }


  @Input() public cropWidth = 300;
  @Input() public cropHeight = 200;

  public originalPicture: string;

  public defaultImage: DefaultImages = DefaultImages.PRODUCTION_SITE;
  @ViewChild('file') public fileInputElement: ElementRef;
  public sourceFileName: string;
  public sourcePicture: string;
  private _plant: ProductionSiteDTO;

  private static dataToBlob(dataURI) {
    // convert base64/URLEncoded data component to raw binary data held in a string
    let byteString;
    if (dataURI.split(',')[0].indexOf('base64') >= 0) {
      byteString = atob(dataURI.split(',')[1]);
    } else {
      byteString = unescape(dataURI.split(',')[1]);
    }
    // separate out the mime component
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    // write the bytes of the string to a typed array
    const ia = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ia], {type: mimeString});
  }


  private static dataToFile(data: string, fileName: string): File {
    const f: any = ImageUploaderComponent.dataToBlob(data);
    f.lastModifiedDate = new Date();
    f.name = fileName;

    return <File>f;
  }

  public onChangeFileInput(input: { target: HTMLInputElement }) {
    if (input.target.files.length === 0) {
      return;
    }

    this.sourceFileName = input.target.files[0].name;
    this.sourcePicture = URL.createObjectURL(input.target.files[0]);

    this.openDialog(this.sourcePicture, this.sourceFileName).afterClosed().subscribe((file) => {
      this.onCloseDialog(file);
    });
  }


  public onClickEdit() {
    this.userTracker.trackEvent('Bild hochladen', 'Widget', 'Bearbeiten');
    this.fileInputElement.nativeElement.click();
  }

  public onClickDelete() {
    this.userTracker.trackEvent('Bild hochladen', 'Widget', 'Entfernen');
    this.rspEndpointService.deleteFile(this.plant.id).then(() => {
      this.notificationService.ok('Bild erfolgreich entfernt.');
      this.originalPicture = undefined;
    }).catch(() => {
      this.notificationService.err('Fehler: Entfernen des Bildes fehlgeschlagen');
    });
  }


  public onErrorImgLoad() {
    this.originalPicture = undefined;
  }


  private openDialog(pictureURL: string, filename: string): MatDialogRef<CropperDialogComponent> {
    return this.dialog.open(CropperDialogComponent, {
      data: {
        width: this.cropWidth,
        height: this.cropHeight,
        imageUrl: pictureURL,
        mimeType: 'image/TODO',
        fileName: filename
      }
    });
  }

  private onCloseDialog(fileURI: string) {
    if (!!fileURI) {
      this.userTracker.trackEvent('Bild hochladen', 'Dialog', 'Hochladen');
      const file: File = ImageUploaderComponent.dataToFile(fileURI, this.sourceFileName);
      this.unloadPicture();
      this.uploadFile(file, this.plant.id); // TODO Plant kann null sein. Fehler fangen oder Plant kachel ausblenden?
    } else {
      this.userTracker.trackEvent('Bild hochladen', 'Dialog', 'Abbruch');
      this.unloadPicture();
    }
  }

  private uploadFile(file: File, plantId: number) {
    this.rspEndpointService.postFileUpload(file, plantId).then(() => {
      this.originalPicture = this.createImageUrl(plantId);
    }).catch(() => {
      this.notificationService.err('Fehler: Konnte das Bild nicht hochladen.');
    });
  }

  private unloadPicture() {
    URL.revokeObjectURL(this.sourcePicture);
    this.sourcePicture = undefined;
    this.fileInputElement.nativeElement.value = '';
  }

  private createImageUrl(productionSiteId: number): string {
    return this.rspEndpointService.createUrlWithQueryParameters(RspEndpointsService.GET_PRODUCTION_SITE_IMAGE, (qs) => {
      qs.push('productionSiteId', productionSiteId);
      qs.push('t', new Date().getTime());
    });
  }

}
